<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Icon from 'dashboard/components-next/icon/Icon.vue';

// Chip desplegable de los filtros de informes, con la misma gramática que
// las cabeceras de Bandeja y Contactos: relleno suave cuando filtra, gris
// cuando no. Sustituye al `multiselect` heredado, que traía su propio
// aspecto y obligaba a una rejilla de columnas de 250px.
//
// Los siete filtros comparten forma —lista de opciones, una clave para el
// texto y otra para identificar— así que sólo cambia la configuración.
const props = defineProps({
  modelValue: { type: [Object, Array], default: null },
  options: { type: Array, default: () => [] },
  // Qué campo de la opción se pinta y cuál la identifica.
  labelKey: { type: String, default: 'name' },
  trackKey: { type: String, default: 'id' },
  placeholder: { type: String, default: '' },
  // Texto fijo delante del valor ("Periodo: Últimos 7 días").
  prefix: { type: String, default: '' },
  icon: { type: String, default: '' },
  multiple: { type: Boolean, default: false },
  // Con `clearable` la lista abre con una opción "Todos" que quita el filtro.
  clearable: { type: Boolean, default: true },
  // Punto de color a la izquierda de la opción (etiquetas).
  colorOf: { type: Function, default: null },
  // Texto de la opción cuando no basta con `labelKey` (etiquetas).
  labelOf: { type: Function, default: null },
});

const emit = defineEmits(['update:modelValue']);

const { t } = useI18n();

const isOpen = ref(false);
const close = () => {
  isOpen.value = false;
};

const optionLabel = option =>
  props.labelOf ? props.labelOf(option) : option?.[props.labelKey];
const optionId = option => option?.[props.trackKey];

const selected = computed(() =>
  props.multiple ? props.modelValue || [] : props.modelValue
);

const isSelected = option =>
  props.multiple
    ? selected.value.some(item => optionId(item) === optionId(option))
    : optionId(selected.value) === optionId(option);

const hasValue = computed(() =>
  props.multiple ? selected.value.length > 0 : Boolean(selected.value)
);

const triggerLabel = computed(() => {
  if (!hasValue.value) return props.placeholder;
  if (!props.multiple) return optionLabel(selected.value);
  const [first, ...rest] = selected.value;
  return rest.length
    ? `${optionLabel(first)} +${rest.length}`
    : optionLabel(first);
});

const pick = option => {
  if (!props.multiple) {
    close();
    emit('update:modelValue', option);
    return;
  }
  const next = isSelected(option)
    ? selected.value.filter(item => optionId(item) !== optionId(option))
    : [...selected.value, option];
  emit('update:modelValue', next);
};

const clear = () => {
  close();
  emit('update:modelValue', props.multiple ? [] : null);
};

const TRIGGER_CLASS =
  'inline-flex items-center gap-[7px] h-8 px-2.5 rounded-lg text-xs whitespace-nowrap max-w-[15rem] border transition-colors';
const PANEL_CLASS =
  'absolute z-30 top-full mt-1.5 ltr:left-0 rtl:right-0 min-w-[238px] pt-1 pb-1.5 rounded-[10px] border border-n-weak bg-n-solid-2 shadow-[0_10px_28px_rgba(20,40,60,.15)] max-h-72 overflow-y-auto';
const ROW_CLASS =
  'flex items-center gap-2 w-full h-[31px] px-3 text-[12.5px] text-left';

const triggerClass = computed(() =>
  hasValue.value
    ? 'bg-n-solid-blue border-n-blue-7 text-n-blue-text font-semibold'
    : 'bg-n-solid-1 border-n-container text-n-slate-11 font-medium hover:bg-n-alpha-1'
);
const rowClass = active =>
  active
    ? 'bg-n-blue-2 text-n-blue-text font-semibold'
    : 'text-n-slate-11 font-medium hover:bg-n-alpha-1';
</script>

<template>
  <div v-on-clickaway="close" class="relative">
    <button
      type="button"
      :class="[TRIGGER_CLASS, triggerClass]"
      @click="isOpen = !isOpen"
    >
      <Icon v-if="icon" :icon="icon" size="size-3.5 flex-shrink-0 opacity-70" />
      <span v-if="prefix" class="font-normal text-n-slate-10">
        {{ prefix }}:
      </span>
      <span class="truncate">{{ triggerLabel }}</span>
      <Icon
        icon="i-lucide-chevron-down"
        size="size-[11px] flex-shrink-0 opacity-55"
      />
    </button>

    <div v-if="isOpen" :class="PANEL_CLASS">
      <button
        v-if="clearable"
        type="button"
        :class="[ROW_CLASS, rowClass(!hasValue)]"
        @click="clear"
      >
        <span class="flex-1 truncate">
          {{ t('REPORT.FILTER_CHIP.ALL') }}
        </span>
        <Icon
          v-if="!hasValue"
          icon="i-lucide-check"
          size="size-3 text-n-blue-text"
        />
      </button>
      <button
        v-for="option in options"
        :key="optionId(option)"
        type="button"
        :class="[ROW_CLASS, rowClass(isSelected(option))]"
        @click="pick(option)"
      >
        <span
          v-if="colorOf"
          class="rounded-full size-2.5 flex-shrink-0 border border-n-weak"
          :style="{ backgroundColor: colorOf(option) }"
        />
        <span class="flex-1 truncate">{{ optionLabel(option) }}</span>
        <Icon
          v-if="isSelected(option)"
          icon="i-lucide-check"
          size="size-3 text-n-blue-text"
        />
      </button>
      <p v-if="!options.length" class="px-3 py-2 mb-0 text-xs text-n-slate-10">
        {{ t('REPORT.FILTER_CHIP.EMPTY') }}
      </p>
    </div>
  </div>
</template>
